import { Component } from '@theme/component';

/**
 * @typedef {Object} StickyConsultationCtaRefs
 * @property {HTMLElement} stickyBar - The floating consultation bar container.
 */

/**
 * Shows a lightweight sticky consultation CTA after the product hero scrolls out of view.
 *
 * @extends {Component<StickyConsultationCtaRefs>}
 */
class StickyConsultationCtaComponent extends Component {
  requiredRefs = ['stickyBar'];

  /** @type {IntersectionObserver | null} */
  #targetObserver = null;

  /** @type {IntersectionObserver | null} */
  #footerObserver = null;

  /** @type {Element | null} */
  #visibilityTarget = null;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {boolean} */
  #hiddenByFooter = false;

  connectedCallback() {
    super.connectedCallback();
    this.#setupObservers();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#targetObserver?.disconnect();
    this.#footerObserver?.disconnect();
  }

  #setupObservers() {
    this.#visibilityTarget = this.#getVisibilityTarget();
    if (!this.#visibilityTarget) return;

    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');

    this.#targetObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (entry.isIntersecting) {
        this.#hiddenByFooter = false;
        this.#hideStickyBar();
        return;
      }

      const rect = entry.target.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top < 0) {
        this.#showStickyBar();
      }
    });

    this.#targetObserver.observe(this.#visibilityTarget);

    if (!footer) return;

    this.#footerObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry || !this.#visibilityTarget) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByFooter = true;
          this.#hideStickyBar();
          return;
        }

        if (!entry.isIntersecting && this.#hiddenByFooter) {
          const targetRect = this.#visibilityTarget.getBoundingClientRect();
          if (targetRect.bottom < 0 || targetRect.top < 0) {
            this.#hiddenByFooter = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#footerObserver.observe(footer);
  }

  /**
   * @returns {Element | null}
   */
  #getVisibilityTarget() {
    const targetSelector = this.dataset.targetSelector;
    if (!targetSelector) return null;

    const section = this.closest('.shopify-section');
    return section?.querySelector(targetSelector) ?? document.querySelector(targetSelector);
  }

  #showStickyBar() {
    if (this.#hiddenByFooter) return;

    this.#isStuck = true;
    this.refs.stickyBar.dataset.stuck = 'true';
  }

  #hideStickyBar() {
    this.#isStuck = false;
    this.refs.stickyBar.dataset.stuck = 'false';
  }
}

if (!customElements.get('sticky-consultation-cta')) {
  customElements.define('sticky-consultation-cta', StickyConsultationCtaComponent);
}
